import { getSession } from 'next-auth/react';

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: any; // For structured data cards
}

export interface MCPClientOptions {
  onMessage: (message: AgentMessage) => void;
  onError: (error: Error) => void;
  onConnected: () => void;
  onDisconnected: () => void;
}

/**
 * MCP Client for StreamableHTTP transport
 * Handles connection to MCP server with JWT auth and streaming responses
 */
export class MCPClient {
  private options: MCPClientOptions;
  private isConnected = false;
  private sessionId: string | undefined;

  constructor(options: MCPClientOptions) {
    this.options = options;
  }

  /**
   * Connect to MCP server with StreamableHTTP
   * This initializes the session and validates connectivity
   */
  async connect(): Promise<void> {
    try {
      // Get JWT token from session
      const session = await getSession();
      if (!session?.idToken) {
        throw new Error('No authentication token available');
      }

      const mcpUrl = process.env.NEXT_PUBLIC_MCP_SERVER_URL;
      if (!mcpUrl) {
        throw new Error('NEXT_PUBLIC_MCP_SERVER_URL environment variable is required');
      }

      // Send initialize request to MCP server
      const baseUrl = mcpUrl.replace(/\/$/, '');
      const url = `${baseUrl}/mcp`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/event-stream',
          'Authorization': `Bearer ${session.idToken}`,
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'initialize',
          params: {
            // MCP protocol version - matches server SDK version
            protocolVersion: '2024-11-05',
            capabilities: {},
            clientInfo: {
              name: 'kreayt-web-client',
              version: '1.0.0',
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to connect: ${response.status}`);
      }

      // Parse response - handle both JSON and SSE formats
      const contentType = response.headers.get('content-type') || '';
      let result: any;

      if (contentType.includes('text/event-stream')) {
        // Parse SSE format: "event: message\ndata: {...}\n\n"
        const text = await response.text();
        console.log('[MCP] Raw SSE response:', text);

        // Extract data line - SSE format has "data: " prefix
        const lines = text.split('\n');
        const dataLine = lines.find(line => line.startsWith('data: '));

        if (!dataLine) {
          console.error('[MCP] No data line found in SSE response. Lines:', lines);
          throw new Error('Invalid SSE response format - no data line found');
        }

        // Remove "data: " prefix and parse JSON
        const jsonStr = dataLine.substring(6); // "data: ".length === 6
        console.log('[MCP] Extracted JSON string:', jsonStr);
        result = JSON.parse(jsonStr);
      } else {
        result = await response.json();
      }

      if (result.error) {
        throw new Error(result.error.message || 'Connection failed');
      }

      // Extract and validate session ID from response headers if present
      const rawSessionId = response.headers.get('x-mcp-session-id');
      if (rawSessionId) {
        // Validate session ID format (UUID v4 pattern)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(rawSessionId)) {
          this.sessionId = rawSessionId;
        } else {
          console.warn('Received invalid session ID format, ignoring');
        }
      }

      // Only mark as connected after successful response parsing
      this.isConnected = true;
      console.log('MCP connection established', this.sessionId ? `with session ${this.sessionId}` : '(stateless)');
      this.options.onConnected();

    } catch (error) {
      console.error('Error connecting to MCP server:', error);
      this.options.onError(error as Error);
      throw error;
    }
  }

  /**
   * Send a message to the agent
   * Uses the legacy /message endpoint for LLM interactions
   */
  async sendMessage(content: string): Promise<void> {
    try {
      const session = await getSession();
      if (!session?.idToken) {
        throw new Error('No authentication token available');
      }

      const mcpUrl = process.env.NEXT_PUBLIC_MCP_SERVER_URL;
      if (!mcpUrl) {
        throw new Error('NEXT_PUBLIC_MCP_SERVER_URL environment variable is required');
      }
      const url = `${mcpUrl}/message`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.idToken}`,
        },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to send message: ${response.status}`);
      }

      const result = await response.json();

      // Emit the response as a message
      // Include data field for navigation and other structured info
      const message: AgentMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: result.content || '',
        timestamp: new Date(),
        data: result.data,
      };
      this.options.onMessage(message);

    } catch (error) {
      console.error('Error sending message:', error);
      this.options.onError(error as Error);
      throw error;
    }
  }

  /**
   * Call an MCP tool directly
   *
   * Note: This method is reserved for Phase 2 implementation when direct tool calling
   * is enabled. Currently unused as all tool interactions go through the /message endpoint.
   */
  async callTool(toolName: string, args: Record<string, unknown>): Promise<any> {
    try {
      const session = await getSession();
      if (!session?.idToken) {
        throw new Error('No authentication token available');
      }

      const mcpUrl = process.env.NEXT_PUBLIC_MCP_SERVER_URL;
      if (!mcpUrl) {
        throw new Error('NEXT_PUBLIC_MCP_SERVER_URL environment variable is required');
      }

      const baseUrl = mcpUrl.replace(/\/$/, '');
      const url = `${baseUrl}/mcp`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/event-stream',
        'Authorization': `Bearer ${session.idToken}`,
      };

      // Include session ID if we have one
      if (this.sessionId) {
        headers['x-mcp-session-id'] = this.sessionId;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: {
            name: toolName,
            arguments: args,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to call tool: ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error.message || 'Tool execution failed');
      }

      return result.result;

    } catch (error) {
      console.error('Error calling tool:', error);
      this.options.onError(error as Error);
      throw error;
    }
  }

  /**
   * Disconnect from MCP server
   */
  disconnect(): void {
    this.isConnected = false;
    this.sessionId = undefined;
    this.options.onDisconnected();
  }

  /**
   * Check if connected
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }
}
